
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface FormData {
  name: string;
  symbol: string;
  initialSupply: string;
  description: string;
}

interface FormFieldsProps {
  formData: FormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSymbolChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function FormFields({ formData, handleChange, handleSymbolChange }: FormFieldsProps) {
  const formatSupply = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimals
    const value = e.target.value.replace(/[^0-9.]/g, '');
    
    // Update the target value
    e.target.value = value;
    
    // Call the main handler
    handleChange(e);
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="name">Community/Creator Name</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-60">Name of your community, project, or creator identity</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          id="name"
          name="name"
          placeholder="e.g., Digital Artists Collective"
          value={formData.name}
          onChange={handleChange}
          required
          maxLength={50}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="symbol">Token Symbol</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-60">Short uppercase identifier (2-6 characters) for your social token</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          id="symbol"
          name="symbol"
          placeholder="e.g., ART"
          value={formData.symbol}
          onChange={handleSymbolChange || handleChange}
          required
          maxLength={6}
          className="uppercase"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="initialSupply">Initial Supply</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-60">Total tokens to create for your community</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          id="initialSupply"
          name="initialSupply"
          type="text"
          inputMode="decimal"
          placeholder="e.g., 1000000"
          value={formData.initialSupply}
          onChange={formatSupply}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Community Description</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Describe your community, its purpose, and what token holders gain access to"
          value={formData.description}
          onChange={(e) => handleChange(e as unknown as React.ChangeEvent<HTMLInputElement>)}
          className="min-h-24"
        />
      </div>
    </>
  );
}
